import React from 'react';
import { render } from 'react-dom';
import { Provider } from 'react-redux';
import { TuuiTheme } from 'tuui';
import { MobileContextProvider } from 'taboola-ultimate-ui';
import 'taboola-ultimate-ui/lib/styles/index.css';
import { abtEventEmitter } from 'modules/taboola-common-frontend-modules/ab-test/ABTEventEmitter';
import { ABTestProvider } from 'modules/taboola-common-frontend-modules/ab-test/ABTestProvider';
import { AccountProvider } from 'modules/taboola-common-frontend-modules/account-configurations/AccountProvider';
import { AppEventsProvider } from 'modules/taboola-common-frontend-modules/app-events-aggregator/AppEventsProvider';
import { PermissionsProvider } from 'modules/taboola-common-frontend-modules/authentication';
import { ChatAgentProvider } from 'modules/taboola-common-frontend-modules/chat-agent/ChatAgentProvider';
import { GoogleApiProvider } from 'modules/taboola-common-frontend-modules/googleApi';
import { I18nProvider } from 'modules/taboola-common-frontend-modules/i18n';
import { useParentAppName } from 'modules/taboola-common-frontend-modules/i18n/hooks/useParentAppName';
import { ModalProvider } from 'modules/taboola-common-frontend-modules/modals';
import { QueryParamBatcherProvider, QueryParamsProvider } from 'modules/taboola-common-frontend-modules/query-params';
import { TracerProvider } from 'modules/taboola-common-frontend-modules/tracer/TracerProvider';
import { WebSocketProvider } from 'modules/taboola-common-frontend-modules/web-socket-provider/WebSocketProvider';
import history from 'services/history';
import { QueryProvider } from 'services/query';
import App from './App';
import { CustomRouter } from './CustomRouter';
import config from './config';
import { CodelessEvents } from './modules/codeless-events/CodelessEvents';
import { CODELESS_EVENTS_ROOT } from './modules/codeless-events/consts/iframeParentConstants';
import { isCodelessEventsApp } from './modules/codeless-events/utils/isCodelessEventsApp';
import { ErrorBoundary } from './modules/errors';
import { ErrorPage } from './modules/errors/components/ErrorPage/ErrorPage';
import { MultiLayerDrawerProvider } from './modules/taboola-common-frontend-modules/multi-layer-drawer/components/MultiLayerDrawerProvider/MultiLayerDrawerProvider';
import Bootstrap from './services/bootstrap/bootstrapper';
import { getStore, initStore } from './store';
import { yahooTheme } from './tuuiYahooTheme';
import './globalStyle/index.scss';

const target = document.querySelector('#root');
const themeMap = {
    yahoo: yahooTheme,
};

const ProvidersWrapper = ({ children }) => {
    const themeName = useParentAppName();
    const tuuiTheme = themeMap[themeName];

    return (
        <QueryProvider>
            <Provider store={getStore()}>
                <CustomRouter history={history}>
                    <QueryParamBatcherProvider>
                        <QueryParamsProvider>
                            <PermissionsProvider>
                                <ABTestProvider eventEmitter={abtEventEmitter}>
                                    <GoogleApiProvider>
                                        <AccountProvider>
                                            <TuuiTheme theme={tuuiTheme}>
                                                <ModalProvider>
                                                    <MultiLayerDrawerProvider>
                                                        <I18nProvider>
                                                            <AppEventsProvider>
                                                                <WebSocketProvider>
                                                                    <MobileContextProvider>
                                                                        <TracerProvider>
                                                                            <ChatAgentProvider>
                                                                                <ErrorBoundary
                                                                                    fallback={
                                                                                        <ErrorPage config={config} />
                                                                                    }
                                                                                >
                                                                                    {children}
                                                                                </ErrorBoundary>
                                                                            </ChatAgentProvider>
                                                                        </TracerProvider>
                                                                    </MobileContextProvider>
                                                                </WebSocketProvider>
                                                            </AppEventsProvider>
                                                        </I18nProvider>
                                                    </MultiLayerDrawerProvider>
                                                </ModalProvider>
                                            </TuuiTheme>
                                        </AccountProvider>
                                    </GoogleApiProvider>
                                </ABTestProvider>
                            </PermissionsProvider>
                        </QueryParamsProvider>
                    </QueryParamBatcherProvider>
                </CustomRouter>
            </Provider>
        </QueryProvider>
    );
};

const ErrorFallback = () => (
    <ProvidersWrapper>
        <ErrorPage config={config} />
    </ProvidersWrapper>
);

const AppWrapper = () => {
    return (
        <ProvidersWrapper>
            <App />
        </ProvidersWrapper>
    );
};

const runError = error => {
    // eslint-disable-next-line no-console
    console.error(error);
    initStore();
    render(<ErrorFallback />, target);
    window.history.pushState(null, 'error', '/error');
};

const runApp = () => {
    if (isCodelessEventsApp()) {
        const codelessContainer = document.getElementById(CODELESS_EVENTS_ROOT);
        render(<CodelessEvents />, codelessContainer);
        return;
    }

    render(<AppWrapper />, target);
};

Bootstrap(initStore, history).then(runApp, runError);
